import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { Task, TaskContextType } from "../lib/types";
import { useQuery } from "@apollo/client";
import { useAuth } from "@/context/auth-context";
import { GET_PLANNER_OCCURRENCES } from "@/graphql/queries/planner-occurrences";
import {
  computePlannerRange,
  isDayInRange,
  mapPlannerOccurrences,
  type PlannerRange,
} from "../lib/occurrences";

const TaskContext = createContext<TaskContextType>({
  tasks: [],
  setTasks: () => {},
  selectedDate: new Date(),
  setSelectedDate: () => {},
  startTime: "08:00",
  setStartTime: () => {},
  filters: {
    activity: [],
    department: [],
    priority: [],
    person: [],
  },
  setFilters: () => {},
  currentTask: null,
  setCurrentTask: () => {},
});

export const useTaskContext = () => useContext(TaskContext);

export const TaskProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { userId, isAuthenticated, isBootstrapping } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());

  // The planner shows one day at a time but loads a window around it, so
  // stepping through the week needs no request per click. The window only
  // moves once the selected day leaves it (state adjusted during render,
  // not in an effect, so there is no render with a stale range).
  const [range, setRange] = useState<PlannerRange>(() =>
    computePlannerRange(new Date()),
  );
  if (!isDayInRange(selectedDate, range)) {
    setRange(computePlannerRange(selectedDate));
  }

  const isSignedIn = !isBootstrapping && isAuthenticated && Boolean(userId);
  const { data, previousData } = useQuery(GET_PLANNER_OCCURRENCES, {
    variables: { fromDate: range.fromDate, toDate: range.toDate },
    skip: !isSignedIn,
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  // While a new window loads, the previous cards stay instead of blinking
  // out; the day filter hides those that do not belong to the new day.
  const occurrences = (data ?? previousData)?.plannerOccurrences;
  const mappedTasks = useMemo(
    () => mapPlannerOccurrences(occurrences ?? []),
    [occurrences],
  );
  const [tasks, setTasks] = useState<Task[]>([]);

  useEffect(() => {
    if (!isSignedIn) {
      setTasks([]);
      return;
    }
    if (!occurrences) return;
    setTasks(mappedTasks);
  }, [isSignedIn, occurrences, mappedTasks]);

  const [startTime, setStartTime] = useState("08:00");
  const [filters, setFilters] = useState({
    activity: [] as string[],
    department: [] as string[],
    priority: [] as string[],
    person: [] as string[],
  });
  const [currentTask, setCurrentTask] = useState<Task | null>(null);

  return (
    <TaskContext.Provider
      value={{
        tasks,
        setTasks,
        selectedDate,
        setSelectedDate,
        startTime,
        setStartTime,
        filters,
        setFilters,
        currentTask,
        setCurrentTask,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};
